/**
 * Partner-preference value translation fallback.
 *
 * Field option labels for the 人生合伙人画像（择偶定位）questionnaire are stored
 * in the DB and many do not have a `label_zh` populated. This dictionary maps
 * the canonical English option text to its Chinese display text so the
 * preference modal renders properly bilingual content even when the DB lacks
 * translations.
 *
 * Lookup is case-insensitive and trims whitespace; we also fall back to the
 * raw English text if no entry is found.
 */

const EN_TO_ZH = {
  // Field labels (preference questionnaire dimensions)
  'age range': '年龄范围',
  'age': '年龄',
  'height': '身高',
  'height requirement': '身高要求',
  'height range': '身高范围',
  'minimum monthly income': '最低月薪',
  'monthly income': '月收入',
  'income': '收入水平',
  'income level': '收入水平/经济能力',
  'minimum education': '最低学历',
  'education': '学历',
  'education level': '学历',
  'marital status': '婚姻状况',
  'marital requirement': '婚姻要求',
  'marriage status': '婚姻状况',
  'housing': '房屋要求',
  'housing requirement': '房屋要求',
  'home': '房屋',
  'parents relationship': '父母相处模式',
  'parents relationship style': '父母相处模式',
  'family structure': '家庭结构',
  'lifestyle': '生活节奏',
  'lifestyle pace': '生活节奏编好',
  'lifestyle preference': '生活节奏编好',
  'pace of life': '生活节奏',
  'communication style': '沟通方式',
  'communication': '沟通方式',
  'conflict resolution': '矛盾处理方式',
  'conflict resolution style': '矛盾处理方式',
  'conflict handling': '矛盾处理方式',
  'children': '子女情况',
  'have children': '生育情况',
  'fertility': '生育（是否愿意生孩子）',
  'fertility / kids': '生育（是否愿意生孩子）',
  'purpose': '报名目的',
  'purpose of registration': '报名目的（是否愿意结婚）',
  'hobbies': '兴趣爱好',
  'interests': '兴趣爱好',
  'long distance': '是否接受异地',
  'long-distance': '是否接受异地',
  'open to long distance': '是否接受异地',
  'job type': '职业类型',
  'occupation': '职业类型',
  'profession': '职业类型',
  'preferred city': '定居城市',
  'settlement city': '定居城市',
  'city': '定居城市',
  'personality': '性格类型',
  'personality type': '性格类型',
  'bottom line': '绝对底线',
  'ideal': '理想标准',
  'acceptable': '可接受范围',
  'car': '车子',
  'car requirement': '车子',
  'has car requirement': '车子',
  'kids': '小孩要求',
  'children requirement': '小孩要求',
  'kids requirement': '小孩要求',
  'child requirement': '小孩要求',
  'marriage timeline': '结婚时间要求',
  'marriage timeline requirement': '结婚时间要求',
  'time to marriage': '结婚时间要求',
  'race': '种族要求',
  'ethnicity': '种族要求',
  'race requirement': '种族要求',
  'ethnicity requirement': '种族要求',
  'long distance requirement': '异地要求',
  'open to long-distance': '异地要求',
  'long distance preference': '异地要求',

  // Marital status
  'unmarried': '未婚',
  'never married': '未婚',
  'single': '单身',
  'married': '已婚',
  'divorced': '离异',
  'divorcing': '正在离婚',
  'separated': '分居',
  'widowed': '丧偶',
  'remarried': '再婚',

  // Education
  'middle school': '初中',
  'high school': '高中',
  'vocational': '中专 / 职高',
  'associate': '大专',
  'college': '大学本科',
  'college/university': '大学本科',
  'university': '大学本科',
  "bachelor's": '本科',
  'bachelor': '本科',
  "master's": '硕士',
  'master': '硕士',
  "master's degree": '硕士',
  "bachelor's degree": '本科',
  'associate degree': '大专',
  'high school diploma': '高中',
  'phd': '博士',
  'doctorate': '博士',
  'doctoral degree': '博士',
  'postdoc': '博士后',

  // Generic / misc
  'none': '无',
  'prefer not to share': '不愿透露',
  'prefer not to say': '不愿透露',

  // Star signs "星座"
  'aries': '白羊座',
  'taurus': '金牛座',
  'gemini': '双子座',
  'cancer': '巨蟹座',
  'leo': '狮子座',
  'virgo': '处女座',
  'libra': '天秤座',
  'scorpio': '天蝎座',
  'sagittarius': '射手座',
  'capricorn': '摩羯座',
  'aquarius': '水瓶座',
  'pisces': '双鱼座',

  // Housing
  'no home': '无房',
  'owns home': '有房',
  'own home': '有房',
  'owns house': '有房',
  'house': '独栋住宅',
  'condo': '公寓',
  'townhouse': '联排别墅',
  'renting': '租房',
  'rents': '租房',
  'multiple properties': '多套房产',
  'mortgage': '按揭中',

  // Income (common ranges already use $ so we keep that, just add unit)
  'under $5k': '5千美金以下',
  'less than $5k': '5千美金以下',
  '$5k - $15k': '5千-1.5万美金',
  '$15k - $25k': '1.5万-2.5万美金',
  '$25k - $50k': '2.5万-5万美金',
  '$50k - $100k': '5万-10万美金',
  '$100k+': '10万美金以上',
  'over $100k': '10万美金以上',
  'over $300k': '30万美金以上',

  // Job type
  'employee': '上班族',
  'office worker': '上班族',
  'salaried': '上班族',
  'freelance': '自由职业',
  'freelancer': '自由职业',
  'business owner': '企业主',
  'entrepreneur': '创业者',
  'self-employed': '个体经营',
  'unemployed': '无业',
  'retired': '退休',
  'student': '学生',
  'civil servant': '公务员',
  'government': '政府部门',

  // Family structure
  'two-parent family': '双亲家庭',
  'single parent': '单亲家庭',
  'single parent family': '单亲家庭',
  'both parents living': '父母双全',
  'reconstituted family': '重组家庭',
  'extended family': '大家庭',

  // Parents relationship
  'have a sense of boundaries': '有边界感',
  'sense of boundaries': '有边界感',
  'boundaries': '有边界感',
  'living with parents': '和父母同住',
  'lives with parents': '和父母同住',
  'parents interfere too much': '父母干涉太多',
  'parents are too involved': '父母干涉太多',

  // Lifestyle / pace
  'relaxed': '松弛有度',
  'easy-going': '松弛有度',
  'busy or well organize': '忙碌或生活规律',
  'busy or well organized': '忙碌或生活规律',
  'busy': '繁忙',
  'well organized': '生活规律',
  'slow': '慢节奏',
  'fast': '快节奏',

  // Communication style
  'communicate directly': '直接沟通',
  'direct communication': '直接沟通',
  'indirect': '委婉沟通',
  'cold war': '冷战',
  'avoid conflict': '回避冲突',
  'hot and cold': '忽冷忽热',

  // Conflict handling
  'admit your mistakes and be honest': '有错就认错、坦诚',
  'admit mistakes': '承认错误',
  'be honest': '坦诚',
  'communicate openly': '多沟通',
  'face problems together': '共同面对问题',
  'run away': '甩锅',
  'blame others': '甩锅',

  // Car
  'has car': '有车',
  'own car': '有车',
  'owns car': '有车',
  'has a car': '有车',
  'no car': '无车',
  'does not have car': '无车',
  'without car': '无车',

  // Children / fertility
  'no children': '不要孩子',
  "don't want children": '不要孩子',
  'want children': '想要孩子',
  'has children': '有孩子',
  'has children (with them)': '有孩子（与他们在一起）',
  'has children with them': '有孩子（与他们在一起）',
  'has children (with other)': '有孩子（与其他人）',
  'has children (independent)': '有孩子（已独立）',
  'prefer not to say': '不便透露',
  'has kids': '有孩子',
  'has kids (with them)': '有孩子（与他们在一起）',
  'children living with them': '孩子与其同住',
  'children not living with them': '孩子未与其同住',
  'open to children': '可以生孩子',
  'kids by choice': '通过科技手段生育',
  'with children': '已婚有孩',
  'without children': '已婚无孩',

  // Marriage timeline
  'anytime': '任何时候',
  'any time': '任何时候',
  'no rush': '不急',
  'flexible': '灵活',
  'within 6 months': '六个月内',
  'within 3 months': '三个月内',
  'within 12 months': '十二个月内',
  'within 2 years': '两年以内',
  'as soon as possible': '尽快',
  'asap': '尽快',

  // Race / ethnicity
  'asian': '亚洲人',
  'east asian': '东亚人',
  'south asian': '南亚人',
  'southeast asian': '东南亚人',
  'chinese': '华人',
  'caucasian': '白人',
  'white': '白人',
  'black': '黑人',
  'african american': '非裔美国人',
  'hispanic': '西班牙裔',
  'latino': '拉丁裔',
  'latina': '拉丁裔',
  'middle eastern': '中东人',
  'native american': '美洲原住民',
  'mixed': '混血',
  'any race': '任何种族',
  'no preference': '无偏好',

  // Long distance / relocation
  'willing': '愿意',
  'willing to relocate': '愿意异地',
  'open': '开放',
  'open to it': '可以接受',
  'not willing': '不愿意',
  'unwilling': '不愿意',

  // Purpose
  'marriage': '结婚',
  'serious relationship': '认真交往',
  'dating': '约会',
  'friendship': '交友',
  'within one year': '一年以内',
  'within 1 year': '一年以内',
  'within 1 year marriage': '一年以内结婚',

  // Personality
  'kind': '善良',
  'kind-hearted': '善良',
  'optimistic': '乐观',
  'sunny personality': '性格阳光',
  'cheerful': '开朗',
  'good character': '人品好',
  'willing to give': '愿意付出',
  'respects women': '尊重女性',
  'mama\u2019s boy': '妈宝男',
  'mamas boy': '妈宝男',
  'no responsibility': '没有责任心',
  'irresponsible': '没有责任心',
  'special hobbies': '特殊嗜好',
  'male chauvinist': '大男子主义',

  // Hobbies
  'sports': '运动',
  'outdoors': '户外',
  'trekking': '徒步',
  'hiking': '徒步',
  'painting': '绘画',
  'travel': '旅游',
  'reading': '阅读',
  'movies': '电影',
  'music': '音乐',
  'cooking': '烹饪',
  'gaming': '游戏',
  'normal hobbies': '正常兴趣爱好',
  'gambling': '赌博',
  'nightclub': '夜店',
  'gaming homebody': '游戏宅男',
  'food & dining': '美食',
  'food and dining': '美食',
  'pets': '宠物',
  'meditation': '冥想',
  'fitness': '健身',
  'photography': '摄影',
  'dancing': '舞蹈',
  'fishing': '钓鱼',
  'yoga': '瑜伽',

  // Appearance / "外表" option values
  'mature type': '成熟型',
  'pretty boy': '帅气型',
  'business elite': '商务精英',
  'sunny & sporty': '阳光运动型',
  'sunny and sporty': '阳光运动型',
  'tall & upright': '高大挺拔',
  'tall and upright': '高大挺拔',
  "doesn't matter if capable": '有能力就行',
  'doesnt matter if capable': '有能力就行',
  'homebody type': '居家型',
  'literary type': '文艺型',
  'gentle type': '温柔型',
  // Female appearance / personality "外表 / 性格" option values
  'elegant & noble': '优雅高贵',
  'elegant and noble': '优雅高贵',
  'elegant': '优雅',
  'gentle lady': '温柔淑女',
  'fashionable': '时尚型',
  'chameleon queen': '百变女王',
  'sweet & cute': '甜美可爱',
  'sweet and cute': '甜美可爱',
  'loli style': '萝莉风格',
  'poised and refined': '端庄优雅',

  // Citizenship status "国籍身份"
  'canadian': '加拿大公民',
  'china citizen': '中国公民',
  'us citizen': '美国公民',
  'us green card': '美国绿卡',
  'other': '其他',
  // Type "类型"
  'career-driven': '事业型',
  'career driven': '事业型',
  'either works': '都可以',
  // Personality "性格"
  'cheerful extrovert': '开朗外向',
  'composed introvert': '沉稳内向',
  // Trait "个性"
  'centered on partner': '以伴侣为中心',
  'self-growth focused': '注重自我成长',
  'self growth focused': '注重自我成长',
  // Expression "表达"
  'direct': '直接',
  // Mood "情绪"
  'emotionally stable': '情绪稳定',
  'romantic & passionate': '浪漫热情',
  'romantic and passionate': '浪漫热情',
  // Conflict handling "处事"
  'cool down first': '先冷静',
  'resolve directly': '直接解决',
  // Spending "消费"
  'man pays all': '男方全付',
  'mostly man': '男方为主',
  'split evenly': '平摊',

  // Languages
  'mandarin': '国语',
  'cantonese': '粤语',
  'english': '英文',
  'english (fluent)': '英文（流利）',
  'english (basic)': '英文（基础）',
  'mandarin, english (fluent)': '国语、英文（流利）',

  // Field labels (used as a fallback when a field has no Chinese label)
  'children preference': '小孩要求',
  'ethnicity preference': '种族要求',
  'marital preference': '婚姻要求',
  'personality preference': '性格要求',
  'look': '外表',
  'appearance': '外表',
  'type': '类型',

  // Locations / regions (a few common ones)
  'los angeles': '洛杉矶',
  'la': '洛杉矶',
  'southern california': '南加州',
  'northern california': '北加州',
  'southern or northern california': '南加或者北加',
  'within usa': '美国境内',
  'usa': '美国',
  'warm city': '温暖城市',

  // ── Profile field values (not just preferences) ──────────────────────────
  // Added for the CRM 推荐给他人 candidate rows, which surface a profile's own
  // 收入 / 学历 / 房产 / 子女 / 国籍 / 行业. These are the exact strings stored in
  // profile_global_custom_values today; anything unmapped falls through to the
  // English text rather than breaking.

  // 月收入 (field 109)
  '$15k - $30k': '1.5万-3万美金',
  '$30k - $50k': '3万-5万美金',
  'over $50k': '5万美金以上',

  // 学历 (field 107)
  'some college': '大专/肄业',
  'some college undergraduate': '大专/肄业',
  "master's degree undergraduate": '硕士在读',

  // 房产 (field 6)
  'have house': '有房',
  'have house in another country': '国外有房',
  "don't have house": '无房',
  'able to buy house': '有购房能力',
  'live with parent': '与父母同住',

  // 子女 (field 90)
  'both parties': '双方均有',
  'yes (independent)': '有（已独立）',
  'yes (living with me)': '有（随我生活）',
  'yes (living with other parent)': '有（随对方生活）',

  // 国籍 / 身份 (field 149)
  'canada pr card': '加拿大枫叶卡',
  'hong kong resident': '香港居民',

  // 行业 (field 5)
  'accounting industry': '会计',
  'administrative staff': '行政',
  'arts industry': '艺术',
  'automotive industry': '汽车',
  'beauty industry': '美容',
  'beauty or hair industry': '美容美发',
  'building contractor': '建筑承包',
  'chief executive officer': '首席执行官',
  'construction engineer': '建筑工程师',
  'construction industry': '建筑',
  'design industry': '设计',
  'doctor': '医生',
  'e-commerce industry': '电商',
  'education industry': '教育',
  'engineer': '工程师',
  'fashion & apparel': '时尚服装',
  'film & media': '影视传媒',
  'film & tv industry': '影视',
  'finance & wealth management': '金融理财',
  'financial services': '金融服务',
  'fitness industry': '健身',
  'food & beverage industry': '餐饮',
  'foreign trade industry': '外贸',
  'freelance industry': '自由职业',
  'government agency': '政府机关',
  'government civil servant': '公务员',
  'health & wellness industry': '健康养生',
  'healthcare industry': '医疗健康',
  'hospital staff': '医院工作人员',
  'hotel management': '酒店管理',
  'housekeeping or domestic services industry': '家政服务',
  'investment': '投资',
  'it industry': '互联网/IT',
  'legal consulting': '法律咨询',
  'legal counsel': '法务',
  'legal profession': '法律',
  'logistics industry': '物流',
  'marketing promotion': '市场营销',
  'mechanic': '技工',
  'media industry': '传媒',
  'medical & pharmaceuticals': '医药',
  'medical aesthetics industry': '医美',
  'military office': '军人',
  'music industry': '音乐',
  'network & telecommunications': '通信网络',
  'nurse': '护士',
  'nursing industry': '护理',
  'other industry': '其他行业',
  'pet industry': '宠物',
  'pharmacy industry': '医药零售',
  'photography industry': '摄影',
  'police officer': '警察',
  'printing or design': '印刷设计',
  'property management': '物业管理',
  'real estate agent': '房产经纪',
  'real estate development': '房地产开发',
  'real estate industry': '房地产',
  'real estate investment': '房产投资',
  'renovation or interior decoration industry': '装修',
  'research industry': '科研',
  'sales industry': '销售',
  'self-media or content creator': '自媒体',
  'senior management': '高级管理',
  'service industry': '服务业',
  'software engineer': '软件工程师',
  'teacher': '教师',
  'technology industry': '科技',
  'tour guide industry': '导游',
  'tourism industry': '旅游',
  'training industry': '培训',
  'warehouse management specialist': '仓储管理',
  'wealth management industry': '财富管理',
};

/**
 * Translate a single English option label to Chinese, returning the original
 * if no translation is known.
 */
export function translateOptionLabel(text, language) {
  if (language !== 'zh') return text;
  if (text === null || text === undefined) return text;
  const str = String(text).trim();
  if (!str) return text;
  // Normalise curly/backtick apostrophes to a straight one so keys like
  // "doesn't matter if capable" match regardless of the source quote char.
  const key = str.toLowerCase().replace(/[‘’′`]/g, "'");
  if (EN_TO_ZH[key]) return EN_TO_ZH[key];
  // Try splitting on common joiners (e.g. "Owns Home, Condo") and translating each part.
  if (/[,，]/.test(str)) {
    return str.split(/[,，]\s*/).map(part => translateOptionLabel(part, language)).join('、');
  }
  return text;
}

export { EN_TO_ZH };

// Append a unit to numeric height (cm) / weight (lb) values — handles single
// values ("179" → "179cm") and ranges ("155 ~ 178" → "155cm ~ 178cm"). Leaves
// non-numeric text (都可以 / 不限 / etc.) untouched. `label`/`name` are matched
// case-insensitively against 身高/height and 体重/weight.
export function appendUnit(label, name, value) {
  if (value == null || value === '' || value === '-' || value === '—') return value;
  const v = String(value);
  if (!/\d/.test(v)) return v;
  const L = `${label || ''} ${name || ''}`.toLowerCase();
  const isHeight = L.includes('身高') || L.includes('height') || L.includes('heigh');
  const isWeight = L.includes('体重') || L.includes('weight');
  if (!isHeight && !isWeight) return v;
  const unit = isHeight ? 'cm' : 'lb';
  if (v.toLowerCase().includes(unit)) return v;
  return v.replace(/(\d+(?:\.\d+)?)/g, `$1${unit}`);
}
